import { Settings2, Volume2, RotateCcw, MessageSquare, Loader2, History, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SectionCard } from "@/components/ui/section-card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  parsePreviousMessages,
  countMessages,
  hasOverLengthMessage,
  MAX_PREVIOUS_MESSAGES,
  MAX_MESSAGE_LENGTH,
} from "@/lib/previous-messages";
import type { MessageRequest, Sender } from "@/types/api";

interface ProjectData {
  id: number;
  name: string;
  prompt_id: string | null;
}

interface MessageConfigProps {
  config: Partial<MessageRequest>;
  onConfigChange: (config: Partial<MessageRequest>) => void;
  projects: ProjectData[];
  senders: Sender[];
  isLoadingProjects: boolean;
  isLoadingSenders: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
  onGenerateAudio: () => void;
  onReset: () => void;
  hideGenerateButtons?: boolean;
  projectLocked?: boolean;
  readOnly?: boolean;
  researchLocked?: boolean;
}

const llmProviderValues = ["openai", "gemini", "anthropic"];
const messageTypeValues = ["invitation", "main", "followup", "end-conversation", "appointment-request", "appointment-request-calendly"];
const scenarioValues = ["sales-classic", "sales-experience", "sales-job-position", "sales-activity-reference", "sales-common-contacts-quantity", "sales-common-contacts-people", "sales-contact-search", "sales-alumni", "sales-combo-cm", "relationship-building", "event-invitation", "abs-strategy", "reference-strategy"];
const writingStyleValues = ["service-listing", "problem-inquiry", "fear-appeal", "before-after", "number-highlighting", "industry-case-study"];
const languageValues = ["pl", "en", "de", "es", "fr", "it", "pt"];
const messageLengthValues = ["short", "medium", "long"];
const formalityLevelValues = ["formal", "semiformal", "informal"];
const genderValues = ["male", "female", "other"];
const ctaOptionValues = ["none", "check-our-social-media", "visit-our-website", "contact-us", "learn-more", "custom-cta"];

export function MessageConfig({
  config,
  onConfigChange,
  projects,
  senders,
  isLoadingProjects,
  isLoadingSenders,
  isGenerating,
  onGenerate,
  onGenerateAudio,
  onReset,
  hideGenerateButtons = false,
  projectLocked = false,
  readOnly = false,
  researchLocked = false,
}: MessageConfigProps) {
  const { t } = useTranslation('generator');
  const isInvitation = config.message_type === "invitation";
  const isAppointmentRequest = config.message_type === "appointment-request";

  const updateConfig = (key: keyof MessageRequest, value: unknown) => {
    const newConfig = { ...config, [key]: value };
    
    if (key === "message_type") {
      if (value === "invitation") {
        newConfig.message_length = "short";
      }
      if (value === "appointment-request") {
        newConfig.scenario = "sales-classic";
      }
      // Clear previous_messages when leaving followup / end-conversation
      if (value !== "followup" && value !== "end-conversation") {
        newConfig.previous_messages = undefined;
        newConfig.previous_messages_raw = undefined;
      }
    }
    
    onConfigChange(newConfig);
  };

  const showPreviousMessages =
    config.message_type === "followup" || config.message_type === "end-conversation";
  const prevRaw = config.previous_messages_raw || "";
  const prevCount = countMessages(prevRaw);
  const prevOverLimit = prevCount > MAX_PREVIOUS_MESSAGES;
  const prevOverLength = hasOverLengthMessage(prevRaw);

  const handlePrevMessagesChange = (value: string) => {
    const parsed = parsePreviousMessages(value);
    onConfigChange({
      ...config,
      previous_messages_raw: value,
      previous_messages: parsed.length > 0 ? parsed : undefined,
    });
  };

  return (
    <SectionCard icon={Settings2} title={t('config.title')}>
      <div className="space-y-5">
        {readOnly && (
          <Badge variant="secondary" className="gap-1.5 w-full justify-center py-1.5">
            <History className="h-3.5 w-3.5" />
            {t('config.historicalConfig', 'Konfiguracja historyczna')}
          </Badge>
        )}
        {/* Project Selection */}
        <div className="space-y-2">
          <Label>{t('config.project')} <span className="text-destructive">{t('config.required')}</span></Label>
          {isLoadingProjects ? (
            <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-secondary/20">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">{t('config.loadingProjects')}</span>
            </div>
          ) : projectLocked ? (
            <div className="flex items-center h-10 px-3 border rounded-md bg-secondary/30 text-sm">
              {config.project_name || <span className="text-muted-foreground">{t('config.noProject')}</span>}
            </div>
          ) : (
            <Select
              disabled={readOnly}
              value={config.project_name}
              onValueChange={(v) => {
                const selectedProject = projects.find(p => p.name === v);
                if (selectedProject) {
                  onConfigChange({
                    ...config,
                    project_name: v,
                    project_id: selectedProject.id,
                  });
                } else {
                  updateConfig("project_name", v);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('config.selectProject')} />
              </SelectTrigger>
              <SelectContent>
                {[...projects].sort((a, b) => a.name.localeCompare(b.name, 'pl')).map((project) => (
                  <SelectItem key={project.id} value={project.name}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* LLM Provider */}
        <div className="space-y-2">
          <Label>{t('config.llmProvider')}</Label>
          <Select
            value={config.llm_provider || "gemini"}
            onValueChange={(v) => updateConfig("llm_provider", v)}
            disabled={readOnly}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('config.selectProvider')} />
            </SelectTrigger>
            <SelectContent>
              {llmProviderValues.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`llmProviders.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Message Type */}
        <div className="space-y-2">
          <Label>{t('config.messageType')} <span className="text-destructive">{t('config.required')}</span></Label>
          <Select
            value={config.message_type}
            onValueChange={(v) => updateConfig("message_type", v)}
            disabled={readOnly}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('config.selectType')} />
            </SelectTrigger>
            <SelectContent>
              {messageTypeValues.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`messageTypes.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Previous messages in sequence (followup / end-conversation only) */}
        {showPreviousMessages && (
          <div className="space-y-2">
            <Label>{t('config.previousMessages')}</Label>
            <Textarea
              value={prevRaw}
              onChange={(e) => handlePrevMessagesChange(e.target.value)}
              placeholder={t('config.previousMessagesPlaceholder')}
              className="min-h-[140px] font-mono text-xs"
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground">{t('config.previousMessagesHelp')}</p>
            {prevCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {t('config.previousMessagesCount', { count: prevCount })}
              </p>
            )}
            {prevOverLimit && (
              <p className="text-xs text-destructive">
                {t('config.previousMessagesWarningMax', { max: MAX_PREVIOUS_MESSAGES })}
              </p>
            )}
            {prevOverLength && (
              <p className="text-xs text-destructive">
                {t('config.previousMessagesWarningLength', { max: MAX_MESSAGE_LENGTH })}
              </p>
            )}
          </div>
        )}



        {/* Scenario */}
        <div className="space-y-2">
          <Label>{t('config.scenario')} <span className="text-destructive">{t('config.required')}</span></Label>
          {isAppointmentRequest ? (
            <div className="flex items-center h-10 px-3 border rounded-md bg-secondary/30 text-sm">
              {t('scenarios.sales-classic')}
            </div>
          ) : (
            <Select
              value={config.scenario}
              onValueChange={(v) => updateConfig("scenario", v)}
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('config.selectScenario')} />
              </SelectTrigger>
              <SelectContent>
                {scenarioValues.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`scenarios.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Sender */}
        <div className="space-y-2">
          <Label>{t('config.sender')} <span className="text-destructive">{t('config.required')}</span></Label>
          {isLoadingSenders ? (
            <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-secondary/20">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">{t('config.loadingSenders')}</span>
            </div>
          ) : senders.length > 0 ? (
            <Select
              value={config.sender}
              onValueChange={(v) => updateConfig("sender", v)}
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('config.selectSender')} />
              </SelectTrigger>
              <SelectContent>
                {senders.map((sender) => (
                  <SelectItem key={sender.id} value={`${sender.name} ${sender.surname}`}>
                    {sender.name} {sender.surname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center h-10 px-3 border rounded-md bg-secondary/30 text-sm text-muted-foreground">
              {config.project_name ? t('config.noSendersProject') : t('config.noSendersSelect')}
            </div>
          )}
        </div>

        {/* Writing Style */}
        <div className="space-y-2">
          <Label>{t('config.writingStyle')} <span className="text-destructive">{t('config.required')}</span></Label>
          <Select
            value={config.writing_style}
            onValueChange={(v) => updateConfig("writing_style", v)}
            disabled={readOnly}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('config.selectStyle')} />
            </SelectTrigger>
            <SelectContent>
              {writingStyleValues.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`writingStyles.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Language */}
        <div className="space-y-2">
          <Label>{t('config.language')} <span className="text-destructive">{t('config.required')}</span></Label>
          <Select
            value={config.language}
            onValueChange={(v) => updateConfig("language", v)}
            disabled={readOnly}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('config.selectLanguage')} />
            </SelectTrigger>
            <SelectContent>
              {languageValues.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`languages.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Gender */}
        <div className="space-y-2">
          <Label>{t('config.recipientGender')} <span className="text-destructive">{t('config.required')}</span></Label>
          <Select
            value={config.sex}
            onValueChange={(v) => updateConfig("sex", v)}
            disabled={readOnly}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('config.selectGender')} />
            </SelectTrigger>
            <SelectContent>
              {genderValues.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`genders.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Message Length */}
        <div className="space-y-2">
          <Label>{t('config.messageLength')} <span className="text-destructive">{t('config.required')}</span></Label>
          {isInvitation ? (
            <div className="flex items-center h-10 px-3 border rounded-md bg-secondary/30 text-sm">
              {t('messageLengths.short')}
            </div>
          ) : (
            <Select
              value={config.message_length}
              onValueChange={(v) => updateConfig("message_length", v)}
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('config.selectLength')} />
              </SelectTrigger>
              <SelectContent>
                {messageLengthValues.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`messageLengths.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Formality Level */}
        <div className="space-y-2">
          <Label>{t('config.formalityLevel')} <span className="text-destructive">{t('config.required')}</span></Label>
          <Select
            value={config.formality_level}
            onValueChange={(v) => updateConfig("formality_level", v)}
            disabled={readOnly}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('config.selectFormality')} />
            </SelectTrigger>
            <SelectContent>
              {formalityLevelValues.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`formalityLevels.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* CTA */}
        <div className="space-y-2">
          <Label>{t('config.cta')} <span className="text-destructive">{t('config.required')}</span></Label>
          <Select
            value={config.cta_option}
            onValueChange={(v) => updateConfig("cta_option", v)}
            disabled={readOnly}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('config.selectCta')} />
            </SelectTrigger>
            <SelectContent>
              {ctaOptionValues.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`ctaOptions.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* CTA Link */}
        {config.cta_option && config.cta_option !== "none" && (
          <div className="space-y-2">
            <Label>{t('config.ctaLink')} <span className="text-destructive">{t('config.required')}</span></Label>
            <Input
              value={config.cta_link || ""}
              onChange={(e) => updateConfig("cta_link", e.target.value)}
              placeholder={t('config.ctaLinkPlaceholder')}
              type="url"
              disabled={readOnly}
            />
          </div>
        )}

        {/* Common contacts count */}
        {config.scenario === "sales-common-contacts-quantity" && (
          <div className="space-y-2">
            <Label>{t('config.commonContactsCount')} <span className="text-destructive">{t('config.required')}</span></Label>
            <Input
              type="number"
              min="1"
              value={config.common_contacts_count || ""}
              onChange={(e) => updateConfig("common_contacts_count", parseInt(e.target.value) || undefined)}
              placeholder={t('config.commonContactsCountPlaceholder')}
              disabled={readOnly}
            />
          </div>
        )}

        {/* Common contact people */}
        {config.scenario === "sales-common-contacts-people" && (
          <div className="space-y-2">
            <Label>{t('config.commonContactsPeople')} <span className="text-destructive">{t('config.required')}</span></Label>
            <Input
              value={config.common_contact_people_raw || ""}
              onChange={(e) => {
                onConfigChange({
                  ...config,
                  common_contact_people_raw: e.target.value,
                  common_contact_people: e.target.value.split(",").map(p => p.trim()).filter(p => p).length > 0 
                    ? e.target.value.split(",").map(p => p.trim()).filter(p => p)
                    : undefined
                });
              }}
              placeholder={t('config.commonContactsPeoplePlaceholder')}
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground">{t('config.commonContactsPeopleHint')}</p>
          </div>
        )}

        {/* Research firmy */}
        <div className="space-y-2 rounded-md border border-border bg-secondary/20 p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              <Label className="cursor-pointer">Research firmy</Label>
            </div>
            <Switch
              checked={(config as any).research_mode !== false}
              onCheckedChange={(v) => onConfigChange({ ...config, ...( { research_mode: v } as any) })}
              disabled={readOnly || researchLocked}
            />
          </div>
          {((config as any).research_mode !== false) && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Kraj</Label>
              <Select
                value={((config as any).research_country as string) || "Poland"}
                onValueChange={(v) => onConfigChange({ ...config, ...( { research_country: v } as any) })}
                disabled={readOnly || researchLocked}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Poland", "Germany", "United Kingdom", "United States", "France", "Spain", "Italy", "Netherlands"].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Pobiera kontekst o firmie odbiorcy podczas scrapowania profilu.
          </p>
        </div>

        {/* Prompt ID */}
        <div className="space-y-2">
          <Label>{t('config.promptId')}</Label>
          <div className="flex items-center h-10 px-3 border rounded-md bg-secondary/30 text-sm">
            {config.prompt_id || <span className="text-muted-foreground">{t('config.selectProjectFirst')}</span>}
          </div>
        </div>

        {/* Action Buttons */}
        {!hideGenerateButtons && !readOnly && (
          <div className="flex flex-col gap-2 pt-2">
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onReset}>
                <RotateCcw className="mr-2 h-4 w-4" />
                {t('config.reset')}
              </Button>
              <Button variant="outline" className="flex-1" onClick={onGenerateAudio} disabled={isGenerating}>
                <Volume2 className="mr-2 h-4 w-4" />
                {t('config.generateAudio')}
              </Button>
            </div>
            <Button
              className="w-full bg-gradient-primary hover:opacity-90"
              onClick={onGenerate}
              disabled={isGenerating || isLoadingProjects}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('config.generating')}
                </>
              ) : (
                <>
                  <MessageSquare className="mr-2 h-4 w-4" />
                  {t('config.generateMessage')}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </SectionCard>
  );
}
